import { BackToHomeButton } from "@/components/booking/BackToHomeButton";
import { AddressRegistration } from "@/components/booking/create/AddressRegistration";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { Loader } from "@/components/ui/loader";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import {
  invalidateBookingQueries,
  useBooking,
} from "@/hooks/queries/use-bookings-query";
import { isCustomerRole } from "@/lib/roles";
import bookingService from "@/lib/services/bookings";
import {
  cancelRatingReminder,
  scheduleRatingReminder,
} from "@/lib/services/rating-reminder";
import ratingService from "@/lib/services/ratings";
import { webSocketService } from "@/lib/services/websocket";
import { showToast } from "@/lib/toast";
import { handleBookingCompletionReview } from "@/lib/utils/store-review";
import { BookingStep2Data } from "@/lib/validation/booking";
import { useAuthStore } from "@/stores/use-auth-store";
import { useBookingJobStore } from "@/stores/use-booking-job-store";
import { useCreateRequestStore } from "@/stores/use-create-request-store";
import FontAwesome from "@react-native-vector-icons/fontawesome";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQueryClient } from "@tanstack/react-query";
import {
  Stack,
  router,
  useFocusEffect,
  useLocalSearchParams,
} from "expo-router";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Keyboard,
  Platform,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

const FUEL_SERVICE_TYPES = [
  "out_of_fuel",
  "misfuel",
  "adblue_contamination",
] as const;

type Step = "rating" | "vehicle_restart" | "recovery_address";

export default function RateBookingScreen() {
  const { id } = useLocalSearchParams();
  const bookingId = Number(id);
  const qc = useQueryClient();
  const { user, theme } = useAuth();
  const setActiveBooking = useAuthStore((state) => state.setActiveBooking);
  const setNewMessage = useBookingJobStore((state) => state.setNewMessage);
  const setFormData = useCreateRequestStore((state) => state.setFormData);
  const [step, setStep] = useState<Step>("rating");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const scrollRef = useRef<ScrollView>(null);
  const commentOffsetY = useRef(0);
  const insets = useSafeAreaInsets();
  const { data: booking, isLoading } = useBooking(bookingId);

  useEffect(() => {
    const showEvent =
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";
    const hideEvent =
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";

    const showSubscription = Keyboard.addListener(showEvent, () => {
      setKeyboardVisible(true);
    });
    const hideSubscription = Keyboard.addListener(hideEvent, () => {
      setKeyboardVisible(false);
    });

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  const scrollToComment = () => {
    setTimeout(() => {
      scrollRef.current?.scrollTo({
        y: Math.max(0, commentOffsetY.current - 16),
        animated: true,
      });
    }, 100);
  };

  const isCustomer = isCustomerRole(user?.role);
  const ratingType = isCustomer ? "customer_to_driver" : "driver_to_customer";
  const rateeName = isCustomer
    ? booking?.driver?.name
    : booking?.customer?.name;
  const rateeId = isCustomer ? booking?.driver_id : booking?.customer_id;
  const isFuelService =
    isCustomer &&
    FUEL_SERVICE_TYPES.includes(
      booking?.service_type as (typeof FUEL_SERVICE_TYPES)[number],
    );

  useFocusEffect(
    useCallback(() => {
      setActiveBooking(null);
      setNewMessage(false);
    }, [setActiveBooking, setNewMessage]),
  );

  const finishFlow = () => {
    invalidateBookingQueries(qc, bookingId);
    webSocketService.disconnect();
    router.dismissAll();
    router.push("/(tabs)");
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      showToast.error("Please select a rating");
      return;
    }

    if (!rateeId || !ratingType) {
      showToast.error("Unable to determine who to rate");
      return;
    }

    try {
      setLoading(true);
      await ratingService.createRating({
        booking_id: bookingId,
        rated_id: rateeId,
        rating,
        comment: comment || undefined,
        type: ratingType,
      });

      await cancelRatingReminder(bookingId);
      showToast.success("Thank you for your feedback!");
      handleBookingCompletionReview();

      if (isFuelService) {
        setStep("vehicle_restart");
        return;
      }

      finishFlow();
    } catch (error: any) {
      if (error.response?.status === 400) {
        finishFlow();
        return;
      }
      showToast.error(
        error.response?.data?.message || "Failed to submit rating",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSkipRating = async () => {
    try {
      setLoading(true);
      await scheduleRatingReminder(bookingId);
    } catch (error: any) {
      showToast.error(error.response?.data?.message || "Failed to skip rating");
    } finally {
      setLoading(false);
    }
    handleBookingCompletionReview();
    finishFlow();
  };

  const handleVehicleStarted = async () => {
    try {
      setLoading(true);
      await bookingService.recordVehicleRestartOutcome(bookingId, true);
      showToast.success("Glad to hear your vehicle started!");
      finishFlow();
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to save your response",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRequestRecovery = () => {
    setFormData({
      service_type: "breakdown_recovery",
      from_address: booking?.from_address || "",
      from_latitude: booking?.from?.latitude
        ? Number(booking.from.latitude)
        : 0,
      from_longitude: booking?.from?.longitude
        ? Number(booking.from.longitude)
        : 0,
      to_address: "",
      to_latitude: 0,
      to_longitude: 0,
    });
    setStep("recovery_address");
  };

  const handleRecoveryAddressSubmit = async (data: BookingStep2Data) => {
    const result = await bookingService.convertToRecovery(bookingId, {
      reason: "Vehicle did not restart after fuel service",
      from_address: data.from_address,
      from_latitude: data.from_latitude,
      from_longitude: data.from_longitude,
      to_address: data.to_address ?? undefined,
      to_latitude: data.to_latitude ?? undefined,
      to_longitude: data.to_longitude ?? undefined,
    });
    showToast.success("Recovery request submitted");
    invalidateBookingQueries(qc, bookingId);
    webSocketService.disconnect();
    router.replace(
      `/bookings/${result.data.recovery_booking_id}/search-driver`,
    );
  };

  if (step === "recovery_address") {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <AddressRegistration
          route={false}
          submitLabel="Request recovery"
          serviceType="breakdown_recovery"
          onBack={() => setStep("vehicle_restart")}
          onComplete={async (data) => {
            try {
              await handleRecoveryAddressSubmit(data);
            } catch (error: any) {
              showToast.error(
                error.response?.data?.message || "Failed to request recovery",
              );
            }
          }}
        />
      </>
    );
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: step === "rating" ? "Rate Experience" : "Vehicle Status",
        }}
      />
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.background }]}
        edges={["top"]}
      >
        {(isLoading || loading) && <Loader />}
        {booking?.user_rating ? (
          <View
            style={[
              {
                backgroundColor: theme.background,
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
              },
            ]}
          >
            <BackToHomeButton />
          </View>
        ) : (
          <>
            <ScrollView
              ref={scrollRef}
              style={styles.scrollView}
              showsVerticalScrollIndicator={false}
              contentContainerStyle={styles.scrollContent}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode="interactive"
              automaticallyAdjustKeyboardInsets
            >
              {step === "rating" ? (
                <>
                  <View style={styles.header}>
                    <FontAwesome
                      name="user-circle"
                      size={64}
                      color={BrandColors.primary}
                    />
                    <ThemedText style={styles.title} type="title">
                      How was your experience?
                    </ThemedText>
                    <ThemedText style={styles.subtitle} type="subtitle">
                      Rate your experience with {rateeName}
                    </ThemedText>
                    <ThemedText style={styles.helpText} type="small">
                      Your feedback helps us improve our service
                    </ThemedText>
                  </View>

                  <View style={styles.ratingContainer}>
                    <ThemedText style={styles.ratingLabel} type="subtitle">
                      Tap to rate
                    </ThemedText>
                    <View style={styles.stars}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <TouchableOpacity
                          key={star}
                          onPress={() => setRating(star)}
                          style={styles.starButton}
                        >
                          <Ionicons
                            name={star <= rating ? "star" : "star-outline"}
                            size={48}
                            color={
                              star <= rating ? "#D97706" : "#ddd"
                            }
                          />
                        </TouchableOpacity>
                      ))}
                    </View>
                    {rating > 0 && (
                      <ThemedText
                        style={styles.ratingText}
                        type="defaultSemiBold"
                      >
                        {rating === 1 && "Poor"}
                        {rating === 2 && "Fair"}
                        {rating === 3 && "Good"}
                        {rating === 4 && "Very Good"}
                        {rating === 5 && "Excellent"}
                      </ThemedText>
                    )}
                  </View>

                  <View
                    style={styles.commentSection}
                    onLayout={(event) => {
                      commentOffsetY.current = event.nativeEvent.layout.y;
                    }}
                  >
                    <ThemedText style={styles.commentLabel} type="subtitle">
                      Tell us more (optional)
                    </ThemedText>
                    <TextInput
                      style={[
                        styles.textArea,
                        { borderColor: theme.border, color: theme.text },
                      ]}
                      placeholder="Share your thoughts about the service..."
                      placeholderTextColor={theme.text + "60"}
                      value={comment}
                      onChangeText={setComment}
                      multiline
                      numberOfLines={6}
                      textAlignVertical="top"
                      onFocus={scrollToComment}
                    />
                  </View>
                </>
              ) : (
                <View style={styles.restartSection}>
                  <Ionicons name="car-sport" size={64} color="#007AFF" />
                  <ThemedText style={styles.title} type="title">
                    Did your vehicle start?
                  </ThemedText>
                  <ThemedText style={styles.subtitle} type="subtitle">
                    Let us know if the fuel service resolved your issue.
                  </ThemedText>
                  <ThemedText style={styles.helpText} type="small">
                    If your vehicle did not start, you can request breakdown
                    recovery from your current location.
                  </ThemedText>
                </View>
              )}
            </ScrollView>

            {!keyboardVisible && (
              <View
                style={[
                  styles.footer,
                  {
                    backgroundColor: theme.background,
                    paddingBottom: insets.bottom + 20,
                  },
                ]}
              >
                {step === "rating" ? (
                  <>
                    <BrandButton
                      onPress={handleSubmitRating}
                      loading={loading}
                      disabled={loading || rating === 0}
                    >
                      {loading ? "Submitting..." : "Submit Rating"}
                    </BrandButton>

                    <BrandButton onPress={handleSkipRating} variant="outline">
                      Skip for now
                    </BrandButton>
                  </>
                ) : (
                  <>
                    <BrandButton
                      onPress={handleVehicleStarted}
                      loading={loading}
                      disabled={loading}
                    >
                      Yes, it started
                    </BrandButton>
                    <BrandButton
                      onPress={handleRequestRecovery}
                      variant="outline"
                      disabled={loading}
                    >
                      No — request recovery
                    </BrandButton>
                  </>
                )}
              </View>
            )}
          </>
        )}
      </SafeAreaView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 20,
    paddingBottom: 24,
    width: "100%",
    maxWidth: 700,
    marginHorizontal: "auto",
  },
  footer: {
    width: "100%",
    maxWidth: 700,
    marginHorizontal: "auto",
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 24,
    gap: 16,
  },
  header: {
    alignItems: "center",
    paddingVertical: 24,
  },
  restartSection: {
    alignItems: "center",
    paddingVertical: 32,
    gap: 12,
  },
  title: {
    fontSize: 24,
    marginTop: 16,
    textAlign: "center",
  },
  subtitle: {
    fontSize: 18,
    marginTop: 8,
    textAlign: "center",
  },
  helpText: {
    fontSize: 14,
    marginTop: 4,
    textAlign: "center",
  },
  ratingContainer: {
    alignItems: "center",
    paddingVertical: 24,
    borderRadius: 16,
  },
  ratingLabel: {
    fontSize: 18,
    marginBottom: 16,
  },
  stars: {
    flexDirection: "row",
    gap: 8,
  },
  starButton: {
    padding: 4,
  },
  ratingText: {
    fontSize: 16,
    fontWeight: "600",
    marginTop: 16,
  },
  commentSection: {
    marginTop: 24,
  },
  commentLabel: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 12,
  },
  textArea: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    height: 120,
  },
});
